import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Snackbar,
  Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import SaveIcon from "@mui/icons-material/Save";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import TaskAltIcon from "@mui/icons-material/TaskAlt";

import MoneyInput from "../../../generic/MoneyInput";
import DialogConfirmation from "../../../generic/DialogConfirmation";
import PaymentRequestDocuments from "./PaymentRequestDocuments";
import PaymentFlowTrail from "./PaymentFlowTrail";
import PaymentDocumentsCard from "./PaymentDocumentsCard";
import { getContractLegal } from "../../../../services/backend/ContractLegalService";
import {
  approvePaymentRequest,
  getPaymentRequest,
  getPaymentRequests,
  notifyCorrection,
  notifyReturns,
  returnDocument,
  reviewDocument,
  submitPaymentRequest,
  undoReturnDocument,
  updatePaymentRequest,
} from "../../../../services/backend/PaymentRequestLegalService";
import { getContractConfig } from "../../../../services/backend/ContractConfigService";
import { getContractField } from "../../../../functions";
import {
  PAYMENT_REQUEST_STATUS,
  PAYMENT_REQUEST_STATUS_UI,
  PERMISSIONS,
  REVIEW_STATUS,
  REVIEW_STATUS_UI,
} from "../../../../constants";
import {
  committedByOtherRequests,
  getApprovalBlockReason,
  getAvailableBudget,
  getCorrectionNotifyBlockReason,
  getInvoiceAmountBlockReason,
  getPaymentRequestBlockReason,
  getReturnsNotifyBlockReason,
  isPostApproval,
  resolvePaymentRequestDocs,
  reviewItemsOf,
} from "./paymentRequestLogic";
import { formatReviewMoment } from "./paymentReviewUi";
import usePaymentCutoff from "../../usePaymentCutoff";
import PaymentCutoffNotice from "../../PaymentCutoffNotice";

// Detalle de una Solicitud de Pago y contenedor del ciclo de revisión (C7).
// Una sola vista para los dos lados:
//   - la empresa diligencia en borrador, presenta y, si le devuelven algo,
//     reemplaza lo devuelto y notifica su corrección;
//   - el supervisor efectivo revisa documento por documento, notifica las
//     devoluciones (lo que abre el ciclo de corrección) y aprueba.
//
// Todo motivo de bloqueo que se muestra al usuario sale de los helpers
// getXxxBlockReason de paymentRequestLogic: el backend es la autoridad y aquí
// solo se da retroalimentación temprana con la MISMA regla, nunca con un texto
// propio que pueda contradecirla.

const currency = (value) => "$" + (Number(value) || 0).toLocaleString("es-CO", { maximumFractionDigits: 0 });

const RETURNED_COLOR = REVIEW_STATUS_UI[REVIEW_STATUS.RETURNED].color;
const APPROVED_COLOR = PAYMENT_REQUEST_STATUS_UI[PAYMENT_REQUEST_STATUS.APPROVED].color;
const IN_CORRECTION_COLOR = PAYMENT_REQUEST_STATUS_UI[PAYMENT_REQUEST_STATUS.IN_CORRECTION].color;

const PaymentRequestForm = () => {
  const { contractCode, requestId } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);

  const [request, setRequest] = useState(null);
  const [contract, setContract] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [rutDocument, setRutDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  // Revisión: `reviewBusyKey` bloquea el documento en curso y `reviewing` las
  // acciones de la solicitud completa.
  const [reviewBusyKey, setReviewBusyKey] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [returnDialog, setReturnDialog] = useState(null);
  const [undoDialog, setUndoDialog] = useState(null);
  const [confirmReturns, setConfirmReturns] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [confirmCorrection, setConfirmCorrection] = useState(false);

  const notify = useCallback(
    (message, severity = "error") => setSnack({ open: true, message, severity }),
    []
  );

  // Fecha de corte (payment-monthly-cutoff): pasado el corte del mes para la
  // naturaleza del contrato, presentar y aprobar quedan deshabilitados con ese
  // motivo (ADMIN exento). El backend lo revalida en SUBMIT y APPROVE.
  const paymentCutoff = usePaymentCutoff();
  const cutoffReason = paymentCutoff.blockReason(contract?.nature);

  useEffect(() => {
    let active = true;
    (async () => {
      const [found, contractData, config, list] = await Promise.all([
        getPaymentRequest(requestId, user),
        getContractLegal(contractCode, user),
        getContractConfig(),
        getPaymentRequests(contractCode, user),
      ]);
      if (!active) return;
      if (!found || !contractData) {
        notify("No fue posible cargar la solicitud");
        navigate(`/contracts/legal/${contractCode}/payments`);
        return;
      }
      setRequest(found);
      setContract(contractData);
      setCatalog(resolvePaymentRequestDocs(config?.paymentRequestDocs));
      setSiblings(list || []);
      setRutDocument(found.rutDocument || null);
      setLoading(false);
    })();
    return () => { active = false; };
  }, [requestId, contractCode]); // eslint-disable-line

  const readOnly = !request || request.status !== PAYMENT_REQUEST_STATUS.DRAFT;
  const inCorrection = request?.status === PAYMENT_REQUEST_STATUS.IN_CORRECTION;
  // Aprobada por supervisión O ya en el recorrido financiero (Causación → ...
  // → Caja): para la empresa y el supervisor es lo mismo —ya no hay nada que
  // revisar ni corregir— y el recorrido se muestra de solo lectura.
  const isApproved = isPostApproval(request?.status);

  // La empresa dueña. El alcance real lo impone el backend desde users/{uid};
  // aquí solo se decide qué se pinta.
  const isCompany = user?.userType === "ENTITY";

  // Quién revisa: SOLO el supervisor EFECTIVO del contrato (el vigente según
  // las novedades, no el histórico supervisorIDs), más ADMIN para soporte en
  // dev. Mismo criterio que canReview del backend (D5).
  const isReviewer = useMemo(() => {
    if (!contract || isCompany) return false;
    if (user?.roles?.includes("ADMIN")) return true;
    const effectiveSupervisors = getContractField(contract.news, contract)("supervisors") || [];
    return !!user?.permissions?.includes(PERMISSIONS.SUPERVISOR_CONTRACT.UPDATE)
      && effectiveSupervisors.some((s) => s?.userId === user.userId);
  }, [contract, user, isCompany]);

  // Las acciones de revisión existen únicamente sobre una solicitud presentada:
  // en corrección la pelota es de la empresa y en borrador no hay nada que
  // revisar. El motivo se explica en el aviso de arriba, no en cada documento.
  const canReviewDocuments = isReviewer && request?.status === PAYMENT_REQUEST_STATUS.SUBMITTED;
  const correcting = isCompany && inCorrection;

  // El RUT vive en la ficha de la entidad, y actualizarlo es un permiso de la
  // EMPRESA sobre su propia ficha (lo portan ENTITY_ADMIN y ENTITY_MEMBER, no
  // CONTRACTOR_LEGAL). Sin él la carga daría 403, así que la acción no se
  // ofrece: el componente explica a quién pedirla.
  const canUpdateEntityDocs = !!user?.permissions?.includes(PERMISSIONS.MY_ENTITY.UPLOAD_DOC);

  // Valor vigente del contrato con novedades, menos lo que las demás
  // solicitudes ya comprometieron. Nada de esto se persiste: el % de ejecución
  // financiera se deriva igual en C8, de las mismas solicitudes.
  //
  // El exceso es BLOQUEANTE (no una advertencia): con `available` se deshabilita
  // tanto el guardado como el envío, y el backend rechaza el valor de todas
  // formas con la misma regla.
  const budgetInfo = useMemo(() => {
    if (!contract) return null;
    const budget = Number(getContractField(contract.news, contract)("contractBudget")) || 0;
    const invoiced = committedByOtherRequests(siblings, requestId);
    const available = getAvailableBudget({ budget, requests: siblings, requestId });
    const amountReason = getInvoiceAmountBlockReason({
      invoiceAmount: request?.invoiceAmount,
      availableBudget: available,
    });
    // El bloqueo solo tiene sentido mientras el valor se puede cambiar: en una
    // solicitud ya presentada, `available` no se cuenta a sí misma y marcarla
    // en rojo diría que sobra un valor que ya nadie puede corregir.
    //
    // Y dentro del borrador, solo el exceso pinta el campo en rojo: que el
    // valor todavía esté vacío no es un error, es un borrador a medio llenar.
    const editing = request?.status === PAYMENT_REQUEST_STATUS.DRAFT;
    const noBalance = editing && available !== null && available <= 0;
    const exceeds =
      editing && (noBalance || (!!Number(request?.invoiceAmount || 0) && !!amountReason));
    return { budget, invoiced, available, exceeds, noBalance, amountReason };
  }, [contract, siblings, requestId, request]);

  const blockReason = useMemo(() => {
    if (!request) return "";
    if (cutoffReason) return cutoffReason;
    return getPaymentRequestBlockReason({
      documents: request.documents,
      annexes: request.annexes,
      invoiceAmount: request.invoiceAmount,
      rutDocument,
      catalog,
      availableBudget: budgetInfo?.available ?? null,
    });
  }, [request, rutDocument, catalog, budgetInfo, cutoffReason]);

  // Motivos de bloqueo de las tres acciones del ciclo. Salen de los helpers
  // compartidos con el servidor y alimentan directamente los tooltips.
  const approvalBlockReason = useMemo(
    () => (request ? (cutoffReason || getApprovalBlockReason({ request, catalog })) : ""),
    [request, catalog, cutoffReason]
  );
  const returnsNotifyBlockReason = useMemo(
    () => (request ? getReturnsNotifyBlockReason({ request, catalog }) : ""),
    [request, catalog]
  );
  const correctionNotifyBlockReason = useMemo(
    () => (request ? getCorrectionNotifyBlockReason({ request, catalog }) : ""),
    [request, catalog]
  );

  const returnedItems = useMemo(
    () => (request ? reviewItemsOf(request, catalog).filter((i) => i.status === REVIEW_STATUS.RETURNED) : []),
    [request, catalog]
  );

  const patch = (fields) => {
    setRequest((prev) => ({ ...prev, ...fields }));
    setDirty(true);
  };

  // El backend responde con la solicitud ya actualizada: la vista se refresca
  // con lo que él persistió y no con lo que el formulario creía tener.
  const applyRequest = (data) => {
    if (!data) return;
    setRequest((prev) => ({ ...prev, ...data }));
    if (data.rutDocument) setRutDocument(data.rutDocument);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updatePaymentRequest(
        requestId,
        {
          invoiceAmount: request.invoiceAmount,
          invoiceNumber: request.invoiceNumber,
          documents: request.documents || {},
          annexes: request.annexes || [],
        },
        user
      );
      if (!result.ok) {
        notify(result.message || "No fue posible guardar los cambios");
        return false;
      }
      setDirty(false);
      notify("Cambios guardados", "success");
      return true;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Se guarda antes de enviar: el backend valida lo persistido, no lo que
      // el formulario tenga en memoria.
      if (dirty) {
        const saved = await updatePaymentRequest(
          requestId,
          {
            invoiceAmount: request.invoiceAmount,
            invoiceNumber: request.invoiceNumber,
            documents: request.documents || {},
            annexes: request.annexes || [],
          },
          user
        );
        if (!saved.ok) {
          notify(saved.message || "No fue posible guardar los cambios");
          return;
        }
        setDirty(false);
      }

      const result = await submitPaymentRequest(requestId, user);
      if (!result.ok) {
        // El backend es la autoridad: si rechaza, su mensaje es el que vale.
        notify(result.message || "No fue posible enviar la solicitud");
        return;
      }
      setRequest((prev) => ({ ...prev, ...result.data }));
      setRutDocument(result.data.rutDocument || rutDocument);
      notify("Solicitud enviada al supervisor", "success");
    } finally {
      setSaving(false);
    }
  };

  // --- Revisión documento por documento --------------------------------------

  const runDocumentAction = async (key, call, successMessage) => {
    setReviewBusyKey(key);
    try {
      const result = await call();
      if (!result.ok) {
        notify(result.message || "No fue posible registrar la revisión");
        return false;
      }
      applyRequest(result.data);
      notify(successMessage, "success");
      return true;
    } finally {
      setReviewBusyKey(null);
    }
  };

  const handleReviewDocument = (key) =>
    runDocumentAction(key, () => reviewDocument(requestId, key, user), "Documento marcado como revisado");

  const handleReturnDocument = async () => {
    const { key, message } = returnDialog;
    setReturnDialog(null);
    await runDocumentAction(
      key,
      () => returnDocument(requestId, key, message.trim(), user),
      "Documento devuelto. Recuerda enviar la notificación de devoluciones"
    );
  };

  const handleUndoReturn = async (key) => {
    setUndoDialog(null);
    await runDocumentAction(
      key,
      () => undoReturnDocument(requestId, key, user),
      "Devolución deshecha"
    );
  };

  // --- Acciones sobre la solicitud completa ----------------------------------

  const runRequestAction = async (call, successMessage) => {
    setReviewing(true);
    try {
      const result = await call();
      if (!result.ok) {
        notify(result.message || "No fue posible completar la acción");
        return;
      }
      applyRequest(result.data);
      notify(successMessage, "success");
    } finally {
      setReviewing(false);
    }
  };

  const handleNotifyReturns = () =>
    runRequestAction(
      () => notifyReturns(requestId, user),
      "Notificación de devoluciones enviada a la empresa"
    );

  const handleApprove = () =>
    runRequestAction(() => approvePaymentRequest(requestId, user), "Solicitud aprobada");

  const handleNotifyCorrection = () =>
    runRequestAction(
      () => notifyCorrection(requestId, user),
      "Corrección notificada al supervisor"
    );

  // --- Corrección de la empresa ----------------------------------------------
  // Cada reemplazo se persiste solo: el backend acepta ÚNICAMENTE los
  // documentos devueltos, los retorna a pendiente e invalida las marcas de
  // revisión (D8/D10).

  const handleCorrectDocument = async (key, file) => {
    const result = await updatePaymentRequest(requestId, { documents: { [key]: file } }, user);
    if (!result.ok) {
      notify(result.message || "No fue posible reemplazar el documento");
      return;
    }
    applyRequest(result.data);
    notify("Documento reemplazado", "success");
  };

  // El documento que vive en la entidad (el RUT) lo acaba de reemplazar el
  // módulo de Entidades. Lo que falta es que ESTA solicitud lo vuelva a leer:
  //   - en corrección, el servidor lo relee y vuelve a congelar `rutDocument`
  //     al retornar el documento a pendiente (D11 de C7);
  //   - en borrador no hay nada que congelar, así que basta releer la solicitud:
  //     GET la resuelve viva desde la entidad.
  const handleEntityDocumentUpdated = async (key) => {
    if (correcting) {
      await handleCorrectDocument(key, {});
      return;
    }
    const fresh = await getPaymentRequest(requestId, user);
    if (!fresh) {
      notify("El documento se actualizó, pero no fue posible recargar la solicitud");
      return;
    }
    setRequest(fresh);
    setRutDocument(fresh.rutDocument || null);
    notify("Documento actualizado en tu empresa", "success");
  };

  const handleCorrectAnnex = async (index, file) => {
    // El anexo conserva id y título: se envía el arreglo completo porque un
    // anexo no tiene ruta propia, y el servidor verifica que los no devueltos
    // no se hayan movido.
    const next = (request.annexes || []).map((annex, i) => (
      i === index ? { ...annex, ...file } : annex
    ));
    const result = await updatePaymentRequest(requestId, { annexes: next }, user);
    if (!result.ok) {
      notify(result.message || "No fue posible reemplazar el anexo");
      return;
    }
    applyRequest(result.data);
    notify("Anexo reemplazado", "success");
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  const statusUi = PAYMENT_REQUEST_STATUS_UI[request.status] || {};

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 6 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/contracts/legal/${contractCode}/payments`)}>
          Volver
        </Button>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Solicitud de pago {request.seq}
        </Typography>
        <Chip
          size="small"
          label={statusUi.label || request.status}
          color={statusUi.color}
          sx={statusUi.sx}
          icon={statusUi.Icon ? <statusUi.Icon /> : undefined}
        />
      </Stack>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Contrato {contractCode} · {contract.entity?.legalName}
      </Typography>

      {(isCompany || isReviewer) && !isApproved && (
        <PaymentCutoffNotice
          cutoff={paymentCutoff}
          natures={[contract.nature]}
          role={isCompany ? "contractor" : "supervisor"}
          subject="factura"
        />
      )}

      {readOnly && !inCorrection && !isApproved && isCompany && (
        <Alert severity="info" sx={{ my: 2 }}>
          Esta solicitud ya fue enviada al supervisor y no puede modificarse.
        </Alert>
      )}

      {/* Ciclo de corrección: la solicitud está en manos de la empresa hasta
          que notifique. El color y la etiqueta salen del mapa de estados. */}
      {inCorrection && (
        <Alert severity={IN_CORRECTION_COLOR} sx={{ my: 2 }}>
          {isCompany
            ? "La solicitud está en corrección: reemplaza los documentos devueltos y envía la notificación de corrección al supervisor. Los documentos que no te devolvieron no se pueden modificar."
            : "La solicitud está en corrección por la empresa: las acciones de revisión se habilitarán cuando notifique su corrección."}
          {request.correction?.startedAt
            ? ` (Devoluciones notificadas el ${formatReviewMoment(request.correction.startedAt)}.)`
            : ""}
        </Alert>
      )}

      {isApproved && (
        <Alert severity={APPROVED_COLOR} sx={{ my: 2 }}>
          Solicitud aprobada
          {request.approvedByName ? ` por ${request.approvedByName}` : ""}
          {request.approvedAt ? ` el ${formatReviewMoment(request.approvedAt)}` : ""}.
          {request.status !== PAYMENT_REQUEST_STATUS.APPROVED
            ? " Está en el recorrido financiero: el detalle está más abajo."
            : " Pendiente de recepción en Causación."}
        </Alert>
      )}

      {/* Recorrido financiero (acta #3), de solo lectura para la empresa, el
          supervisor y el gestor. Las acciones viven en Gestión de Pagos. */}
      {isApproved && (
        <Card variant="outlined" sx={{ my: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recorrido financiero
            </Typography>
            <PaymentFlowTrail request={request} />
          </CardContent>
        </Card>
      )}

      {/* Documentos de supervisión (informe FO-GBS-01 + acta FO-GAD-10): los
          genera el backend al aprobar. Regenerar es del supervisor efectivo (o
          ADMIN), el mismo criterio que revisar. */}
      {isApproved && (
        <PaymentDocumentsCard
          request={request}
          canRegenerate={isReviewer}
          onUpdated={(data) => setRequest((prev) => ({ ...prev, ...data }))}
          onError={notify}
        />
      )}

      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item xs={12} md={5}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Factura
              </Typography>
              <MoneyInput
                fullWidth
                margin="dense"
                label="Valor facturado"
                value={request.invoiceAmount ?? ""}
                readOnly={readOnly}
                disabled={readOnly}
                error={!readOnly && !!budgetInfo?.exceeds}
                helperText={
                  !readOnly && budgetInfo?.available !== null && budgetInfo?.available !== undefined
                    ? `Máximo facturable: ${currency(Math.max(0, budgetInfo.available))}`
                    : undefined
                }
                onChange={(e) => patch({ invoiceAmount: e.target.value ?? null })}
              />
              <TextField
                fullWidth
                margin="dense"
                label="Número de factura"
                value={request.invoiceNumber || ""}
                disabled={readOnly}
                onChange={(e) => patch({ invoiceNumber: e.target.value })}
              />
              {/* Fechas de la factura: INFORMATIVAS (daily 2026-09-14). No
                  condicionan el envío; Contabilidad ordena por emisión y
                  Tesorería por vencimiento en Gestión de Pagos. */}
              <Stack direction="row" spacing={1}>
                <TextField
                  fullWidth
                  margin="dense"
                  type="date"
                  label="Fecha de emisión"
                  value={request.invoiceIssuedAt || ""}
                  disabled={readOnly}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => patch({ invoiceIssuedAt: e.target.value || null })}
                />
                <TextField
                  fullWidth
                  margin="dense"
                  type="date"
                  label="Fecha de vencimiento"
                  value={request.invoiceDueAt || ""}
                  disabled={readOnly}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => patch({ invoiceDueAt: e.target.value || null })}
                />
              </Stack>

              {budgetInfo && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Valor del contrato: <strong>{currency(budgetInfo.budget)}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Facturado en otras solicitudes: <strong>{currency(budgetInfo.invoiced)}</strong>
                  </Typography>
                  {/* Sin valor vigente conocido no hay saldo que mostrar (ni
                      techo que imponer): se omite en vez de afirmar $0. */}
                  {budgetInfo.available !== null && (
                    <Typography
                      variant="body2"
                      color={budgetInfo.noBalance ? "error.main" : "text.secondary"}
                    >
                      Disponible: <strong>{currency(Math.max(0, budgetInfo.available))}</strong>
                    </Typography>
                  )}
                  {/* Bloqueante: el valor no puede pasarse del saldo, ni al
                      guardar el borrador ni al presentar la solicitud. */}
                  {budgetInfo.exceeds && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {budgetInfo.amountReason}
                    </Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {!readOnly && (
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Stack spacing={1}>
                  {/* Guardar también está bloqueado por el exceso: el backend
                      rechaza el valor en el UPDATE, así que dejar el botón
                      activo solo produciría un error al pulsarlo. */}
                  <Tooltip title={budgetInfo?.exceeds ? budgetInfo.amountReason : ""}>
                    <span>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        disabled={saving || !dirty || !!budgetInfo?.exceeds}
                        onClick={handleSave}
                      >
                        Guardar borrador
                      </Button>
                    </span>
                  </Tooltip>
                  {/* El motivo del bloqueo va en el tooltip del botón deshabilitado,
                      mismo patrón del envío a revisión del flujo natural. */}
                  <Tooltip title={blockReason || ""}>
                    <span>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={16} /> : <SendIcon />}
                        disabled={saving || !!blockReason}
                        onClick={() => setConfirmSubmit(true)}
                      >
                        Enviar al Supervisor
                      </Button>
                    </span>
                  </Tooltip>
                  {blockReason && (
                    <Typography variant="caption" color="text.secondary">
                      {blockReason}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Panel de revisión del supervisor. Los dos botones quedan
              deshabilitados con el motivo que dan los helpers: en corrección,
              aprobada o con la revisión incompleta, el tooltip lo explica. */}
          {isReviewer && request.status !== PAYMENT_REQUEST_STATUS.DRAFT && !isApproved && (
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Revisión
                </Typography>
                <Stack spacing={1}>
                  <Tooltip title={approvalBlockReason}>
                    <span>
                      <Button
                        fullWidth
                        variant="contained"
                        color={APPROVED_COLOR}
                        startIcon={reviewing ? <CircularProgress size={16} /> : <TaskAltIcon />}
                        disabled={reviewing || !!approvalBlockReason}
                        onClick={() => setConfirmApprove(true)}
                      >
                        Aprobar solicitud
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={returnsNotifyBlockReason}>
                    <span>
                      <Button
                        fullWidth
                        variant="outlined"
                        color={RETURNED_COLOR}
                        startIcon={reviewing ? <CircularProgress size={16} /> : <AssignmentReturnIcon />}
                        disabled={reviewing || !!returnsNotifyBlockReason}
                        onClick={() => setConfirmReturns(true)}
                      >
                        Enviar notificación de devoluciones
                      </Button>
                    </span>
                  </Tooltip>
                  {/* Solo el motivo de la acción principal: el de la
                      notificación vive en su tooltip, y mezclarlos leería como
                      si faltara algo cuando la solicitud ya está aprobable. */}
                  {approvalBlockReason && (
                    <Typography variant="caption" color="text.secondary">
                      {approvalBlockReason}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Devolver un documento no avisa a la empresa: la notificación de
                    devoluciones es la que cierra la revisión y abre el ciclo de corrección.
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Cierre del ciclo por la empresa. Habilitado solo cuando ya no
              queda ningún documento devuelto sin reemplazar. */}
          {correcting && (
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Corrección
                </Typography>
                <Tooltip title={correctionNotifyBlockReason}>
                  <span>
                    <Button
                      fullWidth
                      variant="contained"
                      color={IN_CORRECTION_COLOR}
                      startIcon={reviewing ? <CircularProgress size={16} /> : <SendIcon />}
                      disabled={reviewing || !!correctionNotifyBlockReason}
                      onClick={() => setConfirmCorrection(true)}
                    >
                      Enviar notificación de corrección
                    </Button>
                  </span>
                </Tooltip>
                {correctionNotifyBlockReason && (
                  <Typography variant="caption" color="text.secondary">
                    {correctionNotifyBlockReason}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={7}>
          <PaymentRequestDocuments
            request={request}
            catalog={catalog}
            rutDocument={rutDocument}
            readOnly={readOnly}
            onDocumentsChange={(documents) => patch({ documents })}
            onAnnexesChange={(annexes) => patch({ annexes })}
            onError={notify}
            canUpdateEntityDocs={canUpdateEntityDocs}
            onEntityDocumentUpdated={handleEntityDocumentUpdated}
            canReview={canReviewDocuments}
            reviewBusyKey={reviewBusyKey}
            onReviewDocument={handleReviewDocument}
            onReturnDocument={(key) => setReturnDialog({ key, message: "" })}
            onUndoReturnDocument={(key) => setUndoDialog({ key })}
            correcting={correcting}
            onCorrectDocument={handleCorrectDocument}
            onCorrectAnnex={handleCorrectAnnex}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <DialogConfirmation
        confirmInfo={{
          open: confirmSubmit,
          title: "Enviar solicitud al supervisor",
          confirm: "Enviar",
        }}
        content={`Se enviará la factura por ${currency(request.invoiceAmount)} con sus soportes. Una vez enviada no podrás modificarla.`}
        handleCloseDialog={(confirmed) => {
          setConfirmSubmit(false);
          if (confirmed) handleSubmit();
        }}
      />

      {/* Devolución de un documento: la observación es obligatoria porque es lo
          único que la empresa va a leer para saber qué rehacer. */}
      <Dialog open={!!returnDialog} onClose={() => setReturnDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>Devolver documento</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            margin="dense"
            label="Observación (requerida)"
            placeholder="Describe el motivo de la devolución de este documento"
            value={returnDialog?.message || ""}
            onChange={(e) => setReturnDialog({ ...returnDialog, message: e.target.value })}
            helperText="La empresa solo verá esta observación para corregir el documento"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnDialog(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color={RETURNED_COLOR}
            disabled={!returnDialog?.message?.trim()}
            onClick={handleReturnDocument}
          >
            Devolver
          </Button>
        </DialogActions>
      </Dialog>

      <DialogConfirmation
        confirmInfo={{
          open: !!undoDialog,
          title: "Deshacer devolución",
          confirm: "Deshacer",
          data: undoDialog,
        }}
        content="El documento volverá al estado pendiente. La devolución no se elimina del historial: queda marcada como deshecha."
        handleCloseDialog={(confirmed, data) => {
          setUndoDialog(null);
          if (confirmed && data) handleUndoReturn(data.key);
        }}
      />

      {/* Notificación de devoluciones: se lista exactamente lo que va a viajar
          en el correo, que es uno solo con todos los documentos devueltos. */}
      <DialogConfirmation
        confirmInfo={{
          open: confirmReturns,
          title: "Enviar notificación de devoluciones",
          confirm: "Enviar",
        }}
        content={
          <Box>
            <Typography variant="body2">
              Se enviará un solo correo a la empresa con estos documentos y sus observaciones.
              La solicitud quedará en corrección y tus acciones de revisión se bloquearán
              hasta que la empresa notifique su corrección.
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
              {returnedItems.map((item) => (
                <li key={item.key}>
                  <Typography variant="body2">{item.label}</Typography>
                </li>
              ))}
            </Box>
          </Box>
        }
        handleCloseDialog={(confirmed) => {
          setConfirmReturns(false);
          if (confirmed) handleNotifyReturns();
        }}
      />

      <DialogConfirmation
        confirmInfo={{
          open: confirmApprove,
          title: "Aprobar solicitud de pago",
          confirm: "Aprobar",
        }}
        content={`Se aprobará la solicitud por ${currency(request.invoiceAmount)} y se avisará a la empresa.`}
        handleCloseDialog={(confirmed) => {
          setConfirmApprove(false);
          if (confirmed) handleApprove();
        }}
      />

      <DialogConfirmation
        confirmInfo={{
          open: confirmCorrection,
          title: "Enviar notificación de corrección",
          confirm: "Enviar",
        }}
        content="El supervisor volverá a revisar la solicitud desde el principio: las marcas de revisión previas quedan sin efecto."
        handleCloseDialog={(confirmed) => {
          setConfirmCorrection(false);
          if (confirmed) handleNotifyCorrection();
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert
          onClose={() => setSnack({ ...snack, open: false })}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PaymentRequestForm;
